import React, { useEffect, useState } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { AppsOutlined, ArrowUpwardOutlined, ArrowDownwardOutlined, CheckCircleOutlined } from '@mui/icons-material';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [stats, setStats]   = useState<any>(null);
  const [apps, setApps]     = useState<any[]>([]);
  const [usage, setUsage]   = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [adminStats, appsList, appUsage] = await Promise.all([
        apiService.getAdminStats().catch(() => null),
        apiService.listApps().catch(() => ({ apps: [] })),
        apiService.getAppUsage().catch(() => ({ usage: [] })),
      ]);
      setStats(adminStats);
      setApps(Array.isArray(appsList) ? appsList : (appsList?.apps || []));
      setUsage(Array.isArray(appUsage) ? appUsage : (appUsage?.usage || []));
    } finally {
      setLoading(false);
    }
  };

  const totalIn    = usage.reduce((s, a) => s + (a.total_input_tokens  || 0), 0);
  const totalOut   = usage.reduce((s, a) => s + (a.total_output_tokens  || 0), 0);
  const activeApps = apps.filter(a => a.is_active).length;

  const fmt = (n: number) =>
    n >= 1_000_000 ? (n / 1_000_000).toFixed(1) + 'M'
    : n >= 1_000   ? (n / 1_000).toFixed(1) + 'K'
    : n.toString();

  const maxUsage = Math.max(...usage.map(a =>
    (a.total_input_tokens || 0) + (a.total_output_tokens || 0)
  ), 1);

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  // Design tokens
  const bg       = isDark ? '#0A0A0A' : '#F5F5F5';
  const card     = isDark ? '#111111' : '#FFFFFF';
  const border   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)';
  const textP    = isDark ? '#EFEFEF' : '#0A0A0A';
  const textS    = isDark ? '#888888' : '#666666';
  const textM    = isDark ? '#555555' : '#AAAAAA';
  const hoverBg  = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const skelBg   = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';

  const metrics = [
    { label: 'Applications', value: apps.length, icon: <AppsOutlined sx={{ fontSize: 16 }} />, sub: null },
    { label: 'Active', value: activeApps, icon: <CheckCircleOutlined sx={{ fontSize: 16 }} />, sub: `${apps.length - activeApps} inactive` },
    { label: 'Input Tokens', value: fmt(totalIn), icon: <ArrowUpwardOutlined sx={{ fontSize: 16 }} />, sub: null },
    { label: 'Output Tokens', value: fmt(totalOut), icon: <ArrowDownwardOutlined sx={{ fontSize: 16 }} />, sub: null },
  ];

  return (
    <Box sx={{ maxWidth: 1200, bgcolor: bg }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 6 }}>
        <Box>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: textM, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
            {dateStr}
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: textP, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            {user?.name?.split(' ')[0] ?? 'Admin'}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: textS, mt: 0.5 }}>
            Platform overview
          </Typography>
        </Box>
      </Box>

      {/* Metric Cards */}
      <Box data-tutorial="dashboard-metrics" sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 6 }}>
        {metrics.map(({ label, value, icon, sub }) => (
          <Box key={label} sx={{
            bgcolor: card,
            border: `1px solid ${border}`,
            borderRadius: '8px',
            p: 3,
          }}>
            {loading ? (
              <>
                <Skeleton width={32} height={32} sx={{ borderRadius: '6px', bgcolor: skelBg, mb: 2 }} />
                <Skeleton width={60} height={36} sx={{ borderRadius: 1, bgcolor: skelBg, mb: 1 }} />
                <Skeleton width={90} height={14} sx={{ borderRadius: 1, bgcolor: skelBg }} />
              </>
            ) : (
              <>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '6px',
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: textS, mb: 2,
                }}>
                  {icon}
                </Box>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: textP, letterSpacing: '-0.04em', lineHeight: 1 }}>
                  {value}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: textM, mt: 0.75, fontWeight: 500, letterSpacing: '0.02em' }}>
                  {label}
                  {sub && <Box component="span" sx={{ color: textM, ml: 1 }}>· {sub}</Box>}
                </Typography>
              </>
            )}
          </Box>
        ))}
      </Box>

      {/* Role Stats — only show if stats available */}
      {(loading || stats) && (
        <Box sx={{
          bgcolor: card,
          border: `1px solid ${border}`,
          borderRadius: '8px',
          display: 'flex',
          mb: 6,
          overflow: 'hidden',
        }}>
          {[
            { label: 'Super Admins', value: stats?.by_role?.super_admin ?? stats?.super_admin ?? 0, color: '#FF1A50' },
            { label: 'Admins',       value: stats?.by_role?.admin ?? stats?.admin ?? 0,             color: '#F59E0B' },
            { label: 'Viewers',      value: stats?.by_role?.viewer ?? stats?.viewer ?? 0,           color: '#888888' },
          ].map(({ label, value, color }, idx, arr) => (
            <Box key={label} sx={{
              flex: 1,
              px: 3, py: 2.5,
              borderRight: idx < arr.length - 1 ? `1px solid ${border}` : 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Box>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: textM, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  {label}
                </Typography>
              </Box>
              {loading
                ? <Skeleton width={32} height={28} sx={{ borderRadius: 1, bgcolor: skelBg }} />
                : <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color, letterSpacing: '-0.03em' }}>
                    {value}
                  </Typography>
              }
            </Box>
          ))}
        </Box>
      )}

      {/* Bottom Panels */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 3 }}>

        {/* Applications */}
        <Box data-tutorial="dashboard-apps" sx={{ bgcolor: card, border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: `1px solid ${border}`,
          }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: textP, letterSpacing: '-0.01em' }}>
              Applications
            </Typography>
            {!loading && (
              <Typography sx={{ fontSize: '0.72rem', color: textM, fontWeight: 500 }}>
                {apps.length} total
              </Typography>
            )}
          </Box>

          {loading ? (
            [0,1,2,3,4].map(i => (
              <Box key={i} sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 2, borderBottom: `1px solid ${border}` }}>
                <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: '6px', flexShrink: 0, bgcolor: skelBg }} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton width="55%" height={14} sx={{ borderRadius: 1, mb: 1, bgcolor: skelBg }} />
                  <Skeleton width="35%" height={12} sx={{ borderRadius: 1, bgcolor: skelBg }} />
                </Box>
                <Skeleton width={48} height={22} sx={{ borderRadius: '4px', bgcolor: skelBg }} />
              </Box>
            ))
          ) : apps.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ color: textM, fontSize: '0.875rem' }}>No applications</Typography>
            </Box>
          ) : (
            apps.slice(0, 7).map((app: any) => {
              const initials = app.app_name?.slice(0, 2).toUpperCase() ?? 'AP';
              const dateLabel = new Date(app.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
              return (
                <Box key={app.app_id || app._id} sx={{
                  px: 3, py: 2.5,
                  display: 'flex', alignItems: 'center', gap: 2,
                  borderBottom: `1px solid ${border}`,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: hoverBg },
                  transition: 'background 0.1s',
                }}>
                  <Box sx={{
                    width: 34, height: 34, flexShrink: 0,
                    borderRadius: '6px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                    border: `1px solid ${border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Typography sx={{ fontSize: '0.67rem', fontWeight: 700, color: textS, letterSpacing: '0.04em' }}>
                      {initials}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{
                      fontSize: '0.875rem', fontWeight: 500, color: textP,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      letterSpacing: '-0.01em',
                    }}>
                      {app.app_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 0.25 }}>
                      {dateLabel}
                    </Typography>
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 0.75,
                    px: 1.25, py: 0.5, borderRadius: '4px', flexShrink: 0,
                    bgcolor: app.is_active
                      ? (isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.08)')
                      : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
                    border: `1px solid ${app.is_active
                      ? (isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.15)')
                      : border}`,
                  }}>
                    <Box sx={{
                      width: 5, height: 5, borderRadius: '50%',
                      bgcolor: app.is_active ? '#22C55E' : textM,
                    }} />
                    <Typography sx={{
                      fontSize: '0.67rem', fontWeight: 600,
                      color: app.is_active ? '#22C55E' : textM,
                      letterSpacing: '0.04em', textTransform: 'uppercase',
                    }}>
                      {app.is_active ? 'Live' : 'Off'}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Token Usage */}
        <Box data-tutorial="dashboard-usage" sx={{ bgcolor: card, border: `1px solid ${border}`, borderRadius: '8px', overflow: 'hidden' }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${border}` }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: textP, letterSpacing: '-0.01em' }}>
              Token Usage
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: textM, mt: 0.25 }}>
              Input / output per application
            </Typography>
          </Box>

          {!loading && usage.length > 0 && (
            <Box sx={{ px: 3, py: 2, display: 'flex', gap: 3, borderBottom: `1px solid ${border}` }}>
              {[{ label: 'Input', color: '#22D3EE' }, { label: 'Output', color: '#A855F7' }].map(({ label, color }) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: textM, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {loading ? (
            [0,1,2,3,4].map(i => (
              <Box key={i} sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${border}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Skeleton width={130} height={14} sx={{ borderRadius: 1, bgcolor: skelBg }} />
                  <Skeleton width={44} height={14} sx={{ borderRadius: 1, bgcolor: skelBg }} />
                </Box>
                <Skeleton width="100%" height={6} sx={{ borderRadius: '3px', bgcolor: skelBg }} />
              </Box>
            ))
          ) : usage.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ color: textM, fontSize: '0.875rem' }}>No usage data</Typography>
            </Box>
          ) : (
            usage.slice(0, 7).map((app: any, idx: number) => {
              const inp = app.total_input_tokens  || 0;
              const out = app.total_output_tokens || 0;
              const tot = inp + out;
              const barPct  = Math.round((tot / maxUsage) * 100);
              const inpFrac = tot > 0 ? (inp / tot) * 100 : 50;

              return (
                <Box key={idx} sx={{
                  px: 3, py: 2.5,
                  borderBottom: `1px solid ${border}`,
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { bgcolor: hoverBg },
                  transition: 'background 0.1s',
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                    <Typography sx={{
                      fontSize: '0.875rem', fontWeight: 500, color: textP,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      maxWidth: '70%', letterSpacing: '-0.01em',
                    }}>
                      {app.app_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: textS, flexShrink: 0, ml: 2 }}>
                      {fmt(tot)}
                    </Typography>
                  </Box>

                  <Box sx={{
                    height: 6,
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                    borderRadius: '3px', overflow: 'hidden',
                  }}>
                    <Box sx={{ width: barPct + '%', height: '100%', display: 'flex', minWidth: barPct > 0 ? 4 : 0 }}>
                      <Box sx={{ flex: inpFrac, bgcolor: '#22D3EE' }} />
                      <Box sx={{ flex: 100 - inpFrac, bgcolor: '#A855F7' }} />
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 3, mt: 1.25 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                      IN <Box component="span" sx={{ color: '#22D3EE', fontWeight: 600, ml: 0.5 }}>{fmt(inp)}</Box>
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: textM }}>
                      OUT <Box component="span" sx={{ color: '#A855F7', fontWeight: 600, ml: 0.5 }}>{fmt(out)}</Box>
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardPage;
